/**
 * HowItWorks — "How CARTIE works with your team"
 *
 * Public marketing surface. Walks enterprise reviewers (CTO / SRE lead /
 * platform engineer / security) through the four autonomy tiers and the
 * 14-day Shadow Mode default — in the same language they use internally.
 *
 * Tied 1:1 to /api/policies/automation/tiers — if the tier definitions
 * ever change, this page auto-updates from the source of truth.
 *
 * Companion pages:
 *  - /policies/automation : where customers configure per-repo tiers
 *  - /code-tracer         : the engine that produces the diffs
 *  - /guarantee           : the 3× ROI policy
 *  - /roadmap             : honest ship status
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ShieldQuestion, ShieldCheck, Shield, ShieldAlert, Eye, ArrowRight, Sparkles,
  GitPullRequest, Slack, Clock, AlertTriangle, CheckCircle2, Users, Lock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  T4: {
    icon: ShieldQuestion,
    color: 'slate',
    headline: 'Detect only',
    subhead: 'CARTIE flags it. Your engineer writes the fix.',
    when_to_use: 'Anything touching auth, payments, security, PII, or one-way-doors.',
    what_happens: [
      'Cost spike fires in Slack with a 3-bullet root cause',
      'Code Tracer points to the exact line + suggested diff',
      'No PR opens. Your engineer copy-pastes the diff (or ignores it).',
    ],
    risk: 'Zero — CARTIE never touches your repo.',
  },
  T3: {
    icon: ShieldCheck,
    color: 'emerald',
    headline: 'Draft PR + Slack',
    subhead: 'CARTIE opens a DRAFT PR. You un-draft and merge.',
    when_to_use: 'Default for every new repo. Trust-building tier.',
    what_happens: [
      'Cost spike + Code Tracer fires identically to T4',
      'CARTIE opens a DRAFT PR on your repo with the diff',
      'Slack pings the configured reviewer team with a 1-line summary',
      'Your engineer reviews, removes the DRAFT flag, merges',
    ],
    risk: 'Low — no merge is possible without a human un-drafting + clicking merge.',
  },
  T2: {
    icon: Shield,
    color: 'amber',
    headline: 'Auto-PR + human merges',
    subhead: 'CARTIE opens a real PR. Your engineer reviews + merges.',
    when_to_use: 'Repos with full CI + code owners. After ~30 days at T3 with zero false-positives.',
    what_happens: [
      'Cost spike fires + diff opens as a regular PR (not draft)',
      'Required reviewers + CI checks gate the merge',
      'Your engineer merges when satisfied',
    ],
    risk: 'Medium — relies on your CI catching anything CARTIE missed.',
  },
  T1: {
    icon: ShieldAlert,
    color: 'rose',
    headline: 'Auto-merge after CI + 24h soak',
    subhead: 'CARTIE opens, CI passes, 24h soak, auto-merges.',
    when_to_use: 'Mature repos with deep test coverage. Opt-in PER repo.',
    what_happens: [
      'Cost spike fires + diff opens as PR',
      'CI runs to completion — must pass',
      '24-hour soak window for your team to override',
      'CARTIE auto-merges after the soak if nobody intervenes',
    ],
    risk: 'High — CARTIE merges without explicit human approval. Customer must opt in per repo.',
  },
};

const colorClasses = (color) => ({
  badge: {
    emerald: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    amber:   'bg-amber-500/10 border-amber-500/40 text-amber-300',
    rose:    'bg-rose-500/10 border-rose-500/40 text-rose-300',
    slate:   'bg-slate-500/10 border-slate-500/40 text-slate-300',
  }[color],
  iconBg: {
    emerald: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    amber:   'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    rose:    'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    slate:   'bg-slate-500/15 text-slate-300 ring-slate-500/30',
  }[color],
  border: {
    emerald: 'border-emerald-500/40',
    amber:   'border-amber-500/40',
    rose:    'border-rose-500/40',
    slate:   'border-slate-500/40',
  }[color],
  accent: {
    emerald: 'text-emerald-400',
    amber:   'text-amber-400',
    rose:    'text-rose-400',
    slate:   'text-slate-400',
  }[color],
});

export default function HowItWorks() {
  const [tiers, setTiers] = useState(null);
  const [shadowDays, setShadowDays] = useState(14);

  useEffect(() => {
    axios.get(`${API}/policies/automation/tiers`)
      .then((r) => {
        if (r.data?.tiers) {
          setTiers(r.data.tiers);
          if (r.data.shadow_mode_default_days) {
            setShadowDays(r.data.shadow_mode_default_days);
          }
        }
      })
      .catch(() => { /* tiers fall through to static TIER_META */ });
  }, []);

  // Order T4 → T3 → T2 → T1 (least to most autonomous — the trust ladder)
  const ladder = ['T4', 'T3', 'T2', 'T1'];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>How CARTIE works with your team · Autonomy tiers + Shadow Mode</title>
        <meta name="description" content="See exactly how CARTIE acts on your codebase — Shadow Mode, Draft PRs, Auto-PRs, and Auto-merge. Per-repo controls. Your team always has the kill-switch." />
      </Helmet>

      {/* Sticky nav */}
      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur">
        <Link to="/" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="how-it-works-logo">
          ← CARTIE
        </Link>
        <div className="flex items-center gap-4 text-xs font-semibold text-slate-400">
          <Link to="/roadmap" className="hover:text-emerald-300" data-testid="how-it-works-nav-roadmap">Roadmap</Link>
          <Link to="/guarantee" className="hover:text-emerald-300" data-testid="how-it-works-nav-guarantee">3× Guarantee</Link>
          <Link to="/login" className="hover:text-emerald-300" data-testid="how-it-works-nav-signin">Sign in</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 py-14 max-w-4xl mx-auto" data-testid="how-it-works-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-5">
          For procurement · SRE · engineering leads
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" data-testid="how-it-works-h1">
          How CARTIE works with your team.{' '}
          <span className="text-emerald-400">You always have the kill-switch.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
          Every action CARTIE takes on your repo runs at a tier <em>you</em> chose,
          inside a Shadow Mode window <em>you</em> can extend forever. There is no
          path where CARTIE merges code without an explicit policy you set.
        </p>

        {/* Quick-trust strip */}
        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-slate-900 ring-1 ring-white/10 p-4" data-testid="trust-strip-shadow">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-emerald-300" />
              <span className="text-xs font-mono uppercase tracking-wide text-emerald-300">Shadow Mode</span>
            </div>
            <p className="mt-2 text-sm text-slate-300">
              Default ON for <strong>{shadowDays} days</strong> on every new repo. CARTIE generates
              the diff internally and emails you a weekly digest — but never opens a PR.
            </p>
          </div>
          <div className="rounded-xl bg-slate-900 ring-1 ring-white/10 p-4" data-testid="trust-strip-default">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-300" />
              <span className="text-xs font-mono uppercase tracking-wide text-emerald-300">Safe default</span>
            </div>
            <p className="mt-2 text-sm text-slate-300">
              New repos default to <strong>T3 (Draft PR + Slack)</strong>. CARTIE never auto-merges
              unless you opt the repo in to T1 explicitly.
            </p>
          </div>
          <div className="rounded-xl bg-slate-900 ring-1 ring-white/10 p-4" data-testid="trust-strip-paths">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-emerald-300" />
              <span className="text-xs font-mono uppercase tracking-wide text-emerald-300">Excluded paths</span>
            </div>
            <p className="mt-2 text-sm text-slate-300">
              Glob-pattern blocklist. <code className="text-emerald-300">src/payments/**</code>,
              auth modules, security boundaries — never touched, regardless of tier.
            </p>
          </div>
        </div>
      </section>

      {/* The 4-tier ladder */}
      <section className="px-4 py-10 max-w-4xl mx-auto" data-testid="how-it-works-tiers">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            The four autonomy tiers
          </h2>
          <p className="mt-3 text-sm text-slate-400 max-w-2xl mx-auto">
            Set per repo. Change anytime. Each tier explicitly defines what CARTIE
            may and may not do in your codebase.
          </p>
        </div>

        <div className="space-y-5">
          {ladder.map((tierId, idx) => {
            const meta = TIER_META[tierId];
            const cls = colorClasses(meta.color);
            const Icon = meta.icon;
            return (
              <article
                key={tierId}
                className={`rounded-2xl bg-slate-900 ring-1 ${cls.border} p-6 sm:p-8 transition-all hover:ring-2`}
                data-testid={`tier-card-${tierId}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ring-1 ${cls.iconBg}`}>
                    <Icon className="h-6 w-6" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider border ${cls.badge}`}>
                        Tier {tierId.slice(1)} · {tierId}
                      </span>
                      {tierId === 'T3' && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/40 text-emerald-300">
                          Safe default
                        </span>
                      )}
                      {tierId === 'T1' && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-rose-500/10 border border-rose-500/40 text-rose-300">
                          Opt-in only
                        </span>
                      )}
                    </div>
                    <h3 className={`mt-2 text-xl font-extrabold ${cls.accent}`}>{meta.headline}</h3>
                    <p className="mt-1 text-sm text-slate-300">{meta.subhead}</p>
                  </div>
                  <div className="hidden sm:block text-right text-xs text-slate-500 font-mono">
                    {idx + 1} of {ladder.length}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-6 pl-0 sm:pl-16">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400">When to use</div>
                    <p className="mt-1.5 text-sm text-slate-200">{meta.when_to_use}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400">Risk profile</div>
                    <p className="mt-1.5 text-sm text-slate-200">{meta.risk}</p>
                  </div>
                </div>

                <div className="mt-5 pl-0 sm:pl-16">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400 mb-2">
                    What actually happens when a spike fires
                  </div>
                  <ol className="space-y-2">
                    {meta.what_happens.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                        <span className={`mt-0.5 flex-shrink-0 h-5 w-5 rounded-full text-[11px] font-bold flex items-center justify-center ${cls.iconBg}`}>
                          {i + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Shadow Mode deep-dive */}
      <section className="px-4 py-12 max-w-4xl mx-auto" data-testid="how-it-works-shadow">
        <div className="rounded-2xl bg-slate-900 bg-gradient-to-br from-emerald-500/5 via-slate-900 to-slate-900 ring-1 ring-emerald-500/30 p-8">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center">
              <Eye className="h-6 w-6 text-emerald-300" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-300">Shadow Mode · {shadowDays}-day default</div>
              <h2 className="mt-1 text-2xl font-extrabold tracking-tight">
                CARTIE earns your trust before opening a single PR.
              </h2>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-2xl">
                For the first <strong>{shadowDays} days</strong> on any new repo (regardless of the
                tier you picked), CARTIE runs in Shadow Mode: it produces every diff and
                Slack notification it would have sent — but the PR is generated internally
                only. You see exactly what CARTIE <em>would</em> have done, and you decide if
                its judgment matches yours. Graduate the repo manually when you&apos;re ready,
                or extend Shadow indefinitely.
              </p>
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-slate-900/60 ring-1 ring-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-300">Day 0</div>
                  <p className="mt-1.5 text-xs text-slate-200">Repo added · tier picked · shadow ON</p>
                </div>
                <div className="rounded-lg bg-slate-900/60 ring-1 ring-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-300">Days 1–{shadowDays}</div>
                  <p className="mt-1.5 text-xs text-slate-200">Weekly Slack digest: &quot;here&apos;s what we would have shipped&quot;</p>
                </div>
                <div className="rounded-lg bg-slate-900/60 ring-1 ring-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-300">Day {shadowDays}+</div>
                  <p className="mt-1.5 text-xs text-slate-200">You graduate (or extend shadow). PRs go live at your chosen tier.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Worked example */}
      <section className="px-4 py-12 max-w-4xl mx-auto" data-testid="how-it-works-example">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">A worked example</h2>
        <p className="mt-3 text-sm text-slate-400 max-w-2xl">
          Friday 3 p.m. Engine #12 (Anomaly Engine) detects $4,200/day spike in your
          Anthropic spend. Repo policy: <strong>T3 + Shadow OFF</strong> (you graduated 6 weeks ago).
        </p>
        <ol className="mt-6 space-y-3">
          {[
            { icon: AlertTriangle, color: 'rose',    text: '15:00 — Anomaly Engine fires. Cost up 4× vs. 30-day baseline.' },
            { icon: Sparkles,      color: 'amber',   text: '15:01 — Code Tracer scans your repo. Finds 3 call sites using claude-opus-4-5 in a tight loop.' },
            { icon: GitPullRequest,color: 'emerald', text: '15:02 — DRAFT PR opens: swap to claude-haiku-4-5 (estimated 67% cost reduction, same task quality on this prompt family).' },
            { icon: Slack,         color: 'sky',     text: '15:02 — Slack ping to #cartie-cost with PR link + 3-line diff summary + estimated savings.' },
            { icon: Users,         color: 'violet',  text: '15:15 — Your senior engineer reviews. Diff looks right. Removes DRAFT flag.' },
            { icon: Clock,         color: 'amber',   text: '15:17 — CI passes. Engineer merges.' },
            { icon: CheckCircle2,  color: 'emerald', text: '15:18 — Cost drops 67%. CARTIE books the $X realized savings in your ledger. Audit log records every step.' },
          ].map((step, i) => {
            const StepIcon = step.icon;
            const cls = colorClasses(step.color);
            return (
              <li key={i} className="flex items-start gap-3 rounded-xl bg-slate-900 ring-1 ring-white/10 p-4">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ring-1 ${cls.iconBg}`}>
                  <StepIcon className="h-5 w-5" />
                </div>
                <p className="text-sm text-slate-200 flex-1">{step.text}</p>
              </li>
            );
          })}
        </ol>
      </section>

      {/* What CARTIE NEVER does */}
      <section className="px-4 py-12 max-w-4xl mx-auto" data-testid="how-it-works-never">
        <div className="rounded-2xl bg-slate-900 ring-1 ring-rose-500/30 p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-rose-500/15 ring-1 ring-rose-500/30 flex items-center justify-center">
              <Lock className="h-5 w-5 text-rose-300" />
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">What CARTIE never does</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm text-slate-200">
            {[
              'Touch files under your `excluded_paths` glob (regardless of tier)',
              'Merge a PR without an explicit T1 opt-in on that specific repo',
              'Push to your default branch directly (always a feature branch + PR)',
              'Modify CI configs, IAM policies, or secrets without T4 escalation',
              'Open PRs during Shadow Mode (the diff stays internal-only)',
              'Take any action your audit log can&apos;t replay step-by-step',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTAs */}
      <section className="px-4 py-16 max-w-4xl mx-auto text-center" data-testid="how-it-works-cta">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
          Ready to set this up for your team?
        </h2>
        <p className="mt-3 text-sm text-slate-400 max-w-xl mx-auto">
          Configure per-repo tiers + shadow durations. Audit log records every change.
        </p>
        <div className="mt-7 flex flex-wrap gap-3 justify-center">
          <Link
            to="/policies/automation"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 text-slate-950 text-sm font-bold hover:bg-emerald-400 transition-colors"
            data-testid="how-it-works-cta-configure"
          >
            Configure my repos
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/bill-detective"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 ring-1 ring-white/20 text-slate-100 text-sm font-semibold hover:bg-slate-800 transition-colors"
            data-testid="how-it-works-cta-tracer"
          >
            See the Bill Detective
          </Link>
          <Link
            to="/guarantee"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 ring-1 ring-white/20 text-slate-100 text-sm font-semibold hover:bg-slate-800 transition-colors"
            data-testid="how-it-works-cta-guarantee"
          >
            3× ROI guarantee
          </Link>
        </div>
        <p className="mt-8 text-[11px] text-slate-500 font-mono">
          Source of truth: <code>/api/policies/automation/tiers</code> · live values rendered above
        </p>
        {tiers && (
          <p className="mt-2 text-[11px] text-slate-500 font-mono" data-testid="how-it-works-live-source">
            {tiers.length} tiers loaded from live policy API · shadow_mode_default_days={shadowDays}
          </p>
        )}
      </section>

      <footer className="border-t border-white/5 mt-8 px-4 py-8 text-center text-[11px] text-slate-500">
        <p>© 2026 CARTIEAI LLC · Texas, USA · <Link to="/roadmap" className="hover:text-emerald-400">Roadmap</Link> · <Link to="/audit-log" className="hover:text-emerald-400">Audit log</Link></p>
      </footer>
    </div>
  );
}
